"use client";

import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

type VocalDiscoveryDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const JOURNEY_STEPS = [
  "Initial private Voice Discovery with Liuba — 30 minutes",
  "Private Progress & Voice Blocker Review with Liuba — 40 minutes",
  "First themed online group session with Liuba",
  "Second themed online group session with Liuba",
  "First personalised Video Feedback check-in reviewed by Liuba",
  "Second personalised Video Feedback check-in reviewed by Liuba",
  "First local Singing Attitude Method exercise session with the confirmed programme coach — 50 minutes",
  "Second local Singing Attitude Method exercise session with the confirmed programme coach — 50 minutes",
];

export function VocalDiscoveryDetailsDialog({ open, onClose }: VocalDiscoveryDetailsDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const previousPurchaseFlowActive = document.body.dataset.purchaseFlowActive;
    document.body.style.overflow = "hidden";
    document.body.dataset.purchaseFlowActive = "true";
    window.dispatchEvent(new CustomEvent("sa:purchase-flow", { detail: { active: true } }));
    const overlay = dialogRef.current?.parentElement;
    const backgroundElements = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== overlay)
      .map((element) => ({
        element,
        inert: element.inert,
        hadInertAttribute: element.hasAttribute("inert"),
        ariaHidden: element.getAttribute("aria-hidden"),
      }));
    const lockBackground = () => {
      backgroundElements.forEach(({ element }) => {
        element.inert = true;
        element.setAttribute("inert", "");
        element.setAttribute("aria-hidden", "true");
      });
    };
    lockBackground();
    // Re-apply after React has reconciled any siblings responding to the
    // purchase-flow event (for example, closing the exit-intent modal).
    const backgroundLockTimer = window.setTimeout(lockBackground, 0);

    const focusTimer = window.setTimeout(() => {
      const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? dialogRef.current)?.focus();
    }, 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.clearTimeout(backgroundLockTimer);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousPurchaseFlowActive === undefined) delete document.body.dataset.purchaseFlowActive;
      else document.body.dataset.purchaseFlowActive = previousPurchaseFlowActive;
      backgroundElements.forEach(({ element, inert, hadInertAttribute, ariaHidden }) => {
        element.inert = inert;
        if (!hadInertAttribute) element.removeAttribute("inert");
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      });
      window.dispatchEvent(new CustomEvent("sa:purchase-flow", { detail: { active: previousPurchaseFlowActive === "true" } }));
      window.setTimeout(() => returnFocusRef.current?.focus(), 0);
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center bg-[#10211e]/78 backdrop-blur-sm sm:items-center sm:p-5"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        data-testid="vocal-discovery-details-dialog"
        className="flex max-h-[100dvh] min-h-[100dvh] w-full flex-col overflow-hidden bg-[#fffaf2] text-[#13211f] shadow-[0_30px_100px_rgba(0,0,0,0.4)] outline-none sm:min-h-0 sm:max-w-3xl sm:rounded-[26px] sm:border sm:border-[#d9c78f]/55"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-[#e2d3bc] px-5 py-4 sm:px-6 sm:py-5">
          <div>
            <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#7a5114]">Complete guided journey</p>
            <h2 id={titleId} className="mt-2 font-serif text-2xl font-semibold leading-tight text-[#123b34] sm:text-3xl">Everything included</h2>
            <p id={descriptionId} className="mt-1 text-sm leading-5 text-[#61706b]">The complete eight-touchpoint Vocal Discovery Package journey.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Vocal Discovery package details"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d8cab6] bg-white text-xl font-bold text-[#13211f] transition hover:border-[#b7892d] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7a5114]"
          >
            ×
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <section aria-labelledby={`${titleId}-journey`}>
            <h3 id={`${titleId}-journey`} className="font-serif text-xl font-semibold text-[#123b34]">Full eight-touchpoint journey</h3>
            <ol className="mt-3 grid gap-2 sm:grid-cols-2">
              {JOURNEY_STEPS.map((step, index) => (
                <li key={step} className="flex gap-3 rounded-[14px] border border-[#e2d3bc] bg-white/72 p-3 text-sm font-semibold leading-5 text-[#53615c]">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d9c78f] text-xs font-black text-[#123b34]" aria-hidden>{index + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <section className="rounded-[16px] border border-[#e2d3bc] bg-[#f7eed8] p-4" aria-labelledby={`${titleId}-scheduling`}>
              <h3 id={`${titleId}-scheduling`} className="font-bold text-[#123b34]">Scheduling and local matching</h3>
              <p className="mt-2 text-sm leading-6 text-[#53615c]">The programme is usually completed over approximately eight weeks. Choose your preferred local location before payment; Liuba will agree the coach, studio and remaining dates after your first online session.</p>
            </section>
            <section className="rounded-[16px] border border-[#e2d3bc] bg-[#f7eed8] p-4" aria-labelledby={`${titleId}-resources`}>
              <h3 id={`${titleId}-resources`} className="font-bold text-[#123b34]">Plan and resources</h3>
              <p className="mt-2 text-sm leading-6 text-[#53615c]">Your personalised coaching plan is an output of the opening assessment, not a ninth touchpoint. Relevant portal exercises and resources are included where available.</p>
            </section>
          </div>

          <section className="mt-3 rounded-[16px] border border-[#d9c78f]/65 bg-[#10211e] p-4 text-[#fffaf2]" aria-labelledby={`${titleId}-next`}>
            <h3 id={`${titleId}-next`} className="font-bold text-[#f5df9f]">What happens after payment?</h3>
            <p className="mt-2 text-sm leading-6 text-white/78">You book the opening private 30-minute online session with Liuba immediately. Liuba then agrees the remaining programme dates, local coach and studio with you.</p>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
